import 'cropperjs/dist/cropper.css';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import Cropper, { ReactCropperElement } from 'react-cropper';
import { useCachedBlobUrl } from '../Services/useBlobDownloader';
import { useAlert } from './AlertContext';
import Button from './Buttons';
import './ImageUploader.css';

type ImageShape = 'round' | 'square' | 'rect';

interface ImageUploaderProps {
  id: string;
  className?: string;
  imageWidth?: string;
  imageHeight?: string;
  imageShape?: ImageShape;
  maxImageSizeMB?: number;
  allowedFormats?: string[];
  onImageChange?: (image: string, id: string) => void;
  preuploadedImage?: string | null;
}

const ImageUploader = ({
  id,
  className = '',
  imageWidth = '150px',
  imageHeight = '150px',
  imageShape = 'round',
  maxImageSizeMB = 1,
  allowedFormats = ['image/jpeg', 'image/png'],
  onImageChange,
  preuploadedImage,
}: ImageUploaderProps) => {
  const { getPublicArtifactUrl } = useCachedBlobUrl();
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [aspectRatio, setAspectRatio] = useState<number | 'custom'>(
    imageShape === 'rect' ? 2.2727 : 1,
  );
  const [customWidth, setCustomWidth] = useState<number>(300);
  const [customHeight, setCustomHeight] = useState<number>(300);
  const cropperRef = useRef<ReactCropperElement | null>(null);
  const [cropperKey, setCropperKey] = useState<number>(0);
  const { showAlert } = useAlert();

  // Max constraints for the exported avatar
  const EXPORT_MAX_SIDE = 384; // cap longest side (shrink if you want even smaller files)
  const EXPORT_MAX_KB = 50; // hard cap in kilobytes

  const dataUrlBytes = (dataUrl: string): number => {
    const base64 = dataUrl.split(',')[1] || '';
    return Math.ceil((base64.length * 3) / 4);
  };

  // Downscale a canvas to fit within maxSide (preserves aspect)
  const downscaleCanvas = (srcCanvas: HTMLCanvasElement, maxSide: number): HTMLCanvasElement => {
    const { width: w, height: h } = srcCanvas;
    const scale = Math.min(1, maxSide / Math.max(w, h));
    if (scale === 1) return srcCanvas;
    const dst = document.createElement('canvas');
    dst.width = Math.max(1, Math.round(w * scale));
    dst.height = Math.max(1, Math.round(h * scale));
    const ctx = dst.getContext('2d')!;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(srcCanvas, 0, 0, dst.width, dst.height);
    return dst;
  };

  // Compress a canvas to JPEG under EXPORT_MAX_KB, iteratively lowering quality & size
  const compressCanvasToDataUrl = (
    canvas: HTMLCanvasElement,
    maxKB: number = EXPORT_MAX_KB,
  ): string => {
    const MIME = 'image/jpeg';
    const maxBytes = maxKB * 1024;
    let work = canvas;
    let quality = 0.9;
    let dataUrl = work.toDataURL(MIME, quality);
    let bytes = dataUrlBytes(dataUrl);
    let guard = 0;

    while (bytes > maxBytes && guard < 16) {
      guard++;
      if (quality > 0.5) {
        quality = Math.max(0.5, quality - 0.1);
      } else {
        // reduce dimensions ~15% and retry with slightly higher quality
        const shrink = document.createElement('canvas');
        shrink.width = Math.max(64, Math.round(work.width * 0.85));
        shrink.height = Math.max(64, Math.round(work.height * 0.85));
        const sctx = shrink.getContext('2d')!;
        sctx.imageSmoothingEnabled = true;
        sctx.imageSmoothingQuality = 'high';
        sctx.drawImage(work, 0, 0, shrink.width, shrink.height);
        work = shrink;
        quality = 0.8;
      }
      dataUrl = work.toDataURL(MIME, quality);
      bytes = dataUrlBytes(dataUrl);
    }
    return dataUrl;
  };

  // Make a compressed dataURL from either a dataURL or an http(s) URL
  const makeCompressedFromSrc = (
    src: string,
    maxSide: number = EXPORT_MAX_SIDE,
    maxKB: number = EXPORT_MAX_KB,
  ): Promise<string> =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const base = document.createElement('canvas');
        base.width = img.naturalWidth || img.width;
        base.height = img.naturalHeight || img.height;
        const ctx = base.getContext('2d')!;
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0);
        const down = downscaleCanvas(base, maxSide);
        resolve(compressCanvasToDataUrl(down, maxKB));
      };
      img.onerror = () => reject(new Error('Image load error'));
      img.src = src;
    });

  useEffect(() => {
    if (!preuploadedImage) {
      if (imageSrc !== null) setImageSrc(null);
      if (onImageChange) onImageChange('', id);
      return undefined;
    }

    const isData = preuploadedImage.startsWith('data:image');
    const isHttp = /^https?:\/\//i.test(preuploadedImage);

    if (isData || isHttp) {
      setImageSrc(preuploadedImage);
      setTimeout(() => setCropperKey((k) => k + 1), 200);
      return undefined;
    }

    // It's an artifact key. The public_file endpoint is authenticated, so a
    // plain <img src> gets a 401; fetch it as an authenticated blob instead.
    const ac = new AbortController();
    let isActive = true;
    let objectUrl: string | null = null;

    getPublicArtifactUrl(preuploadedImage, 'image', { signal: ac.signal })
      .then((blobUrl: string | null) => {
        if (!isActive) {
          if (blobUrl) URL.revokeObjectURL(blobUrl);
          return;
        }
        if (!blobUrl) throw new Error('No blob URL returned');
        objectUrl = blobUrl;
        setImageSrc(blobUrl);
        setTimeout(() => setCropperKey((k) => k + 1), 200);
      })
      .catch(() => {
        // Aborts resolve to null here, so ignore anything after cleanup.
        if (!isActive) return;
        showAlert?.('warning', 'Failed to load preuploaded image.');
        setImageSrc(null);
        if (onImageChange) onImageChange('', id);
      });

    return () => {
      isActive = false;
      ac.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preuploadedImage]);

  useEffect(() => {
    if (imageSrc) {
      setTimeout(() => setCropperKey((prevKey) => prevKey + 1), 200);
    }
  }, [imageSrc]);

  const handleAspectRatioChange = (e: ChangeEvent<HTMLSelectElement>): void => {
    const selectedAspectRatio: number | 'custom' =
      e.target.value === 'custom' ? 'custom' : parseFloat(e.target.value);
    setAspectRatio(selectedAspectRatio);

    setTimeout(() => {
      if (cropperRef.current?.cropper) {
        cropperRef.current.cropper.setAspectRatio(
          selectedAspectRatio === 'custom' ? NaN : selectedAspectRatio,
        );
      }
    }, 100); // ✅ Immediately apply aspect ratio change
  };

  // Apply shape class dynamically
  const shapeClass =
    imageShape === 'round'
      ? 'image-round'
      : imageShape === 'square'
        ? 'image-square'
        : 'image-rect';

  const handleImageUpload = (file: File, uploaderId: string): void => {
    if (!file) return;

    // Validate file size (raw file guard)
    if (file.size / 1024 / 1024 > maxImageSizeMB) {
      showAlert?.('warning', `File size exceeds ${maxImageSizeMB}MB limit.`);
      return;
    }

    if (!allowedFormats.includes(file.type)) {
      showAlert?.('warning', `Invalid file format. Allowed: ${allowedFormats.join(', ')}`);
      return;
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onloadend = async () => {
      try {
        const rawDataUrl = reader.result as string; // large dataURL
        const tiny = await makeCompressedFromSrc(rawDataUrl); // ⬅ compress to ~≤50KB
        setImageSrc(tiny);
        if (onImageChange) onImageChange(tiny, uploaderId);
      } catch {
        showAlert?.('warning', 'Failed to process image.');
      }
    };
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>, uploaderId: string): void => {
    const file = event.target.files?.[0];
    if (!file) return;

    handleImageUpload(file, uploaderId);
    // ✅ Reset the specific file input to allow re-uploading the same image
    event.target.value = '';
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];
    handleImageUpload(file, id);
  };

  // Target size based on aspect/custom settings
  const saveCroppedImage = (): void => {
    if (!cropperRef.current) return;
    const cropper = cropperRef.current.cropper;

    const { naturalWidth, naturalHeight } = cropper.getImageData();
    let targetW: number;
    let targetH: number;

    if (aspectRatio !== 'custom') {
      const ratio = Number(aspectRatio) || 1;
      if (naturalWidth / naturalHeight > ratio) {
        targetH = naturalHeight;
        targetW = Math.round(targetH * ratio);
      } else {
        targetW = naturalWidth;
        targetH = Math.round(targetW / ratio);
      }
    } else {
      targetW = Math.max(1, Number(customWidth) || 300);
      targetH = Math.max(1, Number(customHeight) || 300);
    }

    try {
      // 1) Cropped canvas at target size
      const initialCanvas = cropper.getCroppedCanvas({
        width: targetW,
        height: targetH,
        imageSmoothingEnabled: true,
        imageSmoothingQuality: 'high',
      });

      // 2) Downscale to cap dimensions
      const down = downscaleCanvas(initialCanvas, EXPORT_MAX_SIDE);

      // 3) Compress to ≤ 50KB
      const tiny = compressCanvasToDataUrl(down, EXPORT_MAX_KB);

      setImageSrc(tiny);
      if (onImageChange) onImageChange(tiny, id);
      setIsEditing(false);
    } catch {
      showAlert?.('warning', 'Failed to edit image. Please re-upload it.');
    }
  };

  // Delete Image
  const deleteImage = (event: React.MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    setImageSrc(null);
    // propagate empty value so parent onSave sends ""
    if (onImageChange) onImageChange('', id);
    // ✅ Reset the specific file input to allow re-uploading the same image
    const fileInput = document.getElementById(`file-input-${id}`) as HTMLInputElement | null;
    if (fileInput) {
      fileInput.value = '';
    }
  };

  return (
    <div
      id={id}
      className={`image-uploader ${className} ${shapeClass}`}
      style={{ width: imageWidth, height: imageHeight }}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={() => {
        if (!imageSrc) {
          const fileInput = document.getElementById(`file-input-${id}`) as HTMLInputElement | null;
          if (fileInput) {
            fileInput.click();
          }
        }
      }}
    >
      <input
        id={`file-input-${id}`}
        type="file"
        accept="image/*"
        onChange={(event) => handleFileChange(event, id)}
        hidden
      />

      {imageSrc ? (
        <div
          key={imageSrc}
          className={`image-container ${shapeClass}`}
          style={{ width: imageWidth, height: imageHeight }}
        >
          <img
            src={imageSrc}
            alt="Uploaded"
            className="uploaded-image"
            style={{ objectFit: 'cover', width: '100%', height: '100%' }}
          />
          <div className="hover-buttons">
            <button
              className="edit-button"
              onClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
              }}
            >
              <i className="fa-solid fa-pen-to-square"></i>
            </button>
            <button className="img-delete-button" onClick={deleteImage}>
              <i className="fa-solid fa-trash-can"></i>
            </button>
          </div>
        </div>
      ) : (
        <div className="upload-icon">
          <span>
            <i className="fa-solid fa-camera-retro"></i>
          </span>
        </div>
      )}
      {/* Inline Image Editor */}
      {isEditing && imageSrc && (
        <div className="image-editor-overlay">
          <div className="aspect-ratio-selector d-flex flex-row align-center gap mb-1">
            <label>Aspect Ratio:</label>
            <div className="aspect-select">
              <select value={String(aspectRatio)} onChange={handleAspectRatioChange}>
                <option value="1">1:1</option>
                <option value="1.7778">16:9</option>
                <option value="1.3333">4:3</option>
                <option value="2.2727">25:11</option>
                <option value="custom">Custom</option>
              </select>
            </div>
          </div>
          {/* ✅ Ensure Cropper initializes properly */}
          <Cropper
            ref={cropperRef}
            src={imageSrc}
            style={{ width: '100%' }}
            zoomTo={0}
            initialAspectRatio={aspectRatio === 'custom' ? NaN : aspectRatio}
            aspectRatio={aspectRatio === 'custom' ? NaN : aspectRatio}
            preview=".img-preview"
            viewMode={1}
            minCropBoxHeight={50}
            minCropBoxWidth={50}
            background={false}
            responsive={true}
            autoCropArea={1}
            checkOrientation={false}
            guides={true}
            movable={true}
            cropBoxResizable={true}
          />

          <Button variant="primary" size="small" onClick={saveCroppedImage}>
            Save
          </Button>
          <Button variant="danger" size="small" onClick={() => setIsEditing(false)}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
